import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { reactHookForm } from '@yola/ws-ui';
import { blocks, template, i18next, site } from '@yola/ws-sdk';
import dashesToCapitalizeSentence from '../../utils/dashes-to-capitalize-sentence';
import {
  getCachedUserInput,
  setCachedUserInput,
  clearCachedUserInput,
} from '../helpers/block-generation-cache';
import { fieldNames } from '../constants/block-generation';

const { useForm } = reactHookForm;

const CATEGORIES_WITH_BUSINESS_DESCRIPTION = ['about us', 'headline'];
const MAX_USER_INPUT_CHARS = 1000;

const getUserInputCaptions = (blockCategory) => {
  switch (blockCategory) {
    case 'about us':
      return { label: i18next.t('Describe your business') };
    case 'call to action':
      return {
        label: i18next.t('Describe page purpose'),
        statusText: i18next.t('Clearly state the main objective for this page'),
      };
    case 'FAQ':
      return {
        label: i18next.t('List common FAQ'),
        statusText: i18next.t('List answers to common questions people frequently ask you about'),
      };
    case 'headline':
      return { label: i18next.t('Describe your business') };
    case 'menu':
      return {
        label: i18next.t('List your menu'),
        statusText: i18next.t(
          'Give a brief overview of your menu, including item names and prices'
        ),
      };
    case 'pricing':
      return {
        label: i18next.t('List your packages'),
        statusText: i18next.t('Provide a brief overview of your packages and prices'),
      };
    case 'products':
      return {
        label: i18next.t('List your products'),
        statusText: i18next.t(
          'Provide a brief overview of your products, including the product names and prices'
        ),
      };
    case 'services':
      return {
        label: i18next.t('List your services'),
        statusText: i18next.t('Summarize your services using a few key phrases or keywords'),
      };
    case 'team':
      return {
        label: i18next.t('List your team'),
        statusText: i18next.t('Provide a list of your team members along with their roles'),
      };
    case 'testimonials':
      return {
        label: i18next.t('List a few customer quotes'),
        statusText: i18next.t('Highlight key points to emphasize in the generated testimonials'),
      };
    case 'why choose us':
      return {
        label: i18next.t('What sets you apart?'),
        statusText: i18next.t(
          'Highlight what sets you apart from competitors using a few key phrases or keywords'
        ),
      };
    case 'contacts':
    case 'forms':
    case 'gallery':
    case 'news':
    default:
      return null;
  }
};

const getFormFields = ({ control, supportedCategoriesForGeneration, selectedBlockCategory }) => {
  const options = supportedCategoriesForGeneration.map((value) => ({
    value,
    label: dashesToCapitalizeSentence(template.accessors.getMetaTranslations(value)),
  }));
  const userInputCaptions = getUserInputCaptions(selectedBlockCategory);

  const defaultFields = {
    [fieldNames.BLOCK_CATEGORY]: {
      control,
      options,
      placeholder: i18next.t('Select category'),
      rules: {
        required: i18next.t('This field is required'),
      },
      // value is needed to trigger auto-scroll to selected option
      value: options.find(({ value }) => value === selectedBlockCategory) || null,
    },
  };

  if (!userInputCaptions) {
    return defaultFields;
  }

  return {
    ...defaultFields,
    [fieldNames.USER_INPUT]: {
      control,
      ...userInputCaptions,
      rules: {
        maxLength: {
          value: MAX_USER_INPUT_CHARS,
          message: i18next.t(`Maximum number of characters {number}`, {
            number: MAX_USER_INPUT_CHARS,
          }),
        },
      },
    },
  };
};

const useBlockGenerationForm = (initialBlockCategory, initialUserInput) => {
  const isFirstRun = useRef(false);

  const supportedCategoriesForGeneration = useSelector(
    blocks.selectors.getSupportedCategoriesForRegeneration
  );
  const { businessDescription = '' } = useSelector(site.selectors.getSettings);

  const defaultValues = useMemo(() => {
    const defaultBlockCategory = supportedCategoriesForGeneration.includes(initialBlockCategory)
      ? initialBlockCategory
      : '';
    const defaultUserInput =
      initialUserInput ||
      getCachedUserInput(defaultBlockCategory) ||
      (CATEGORIES_WITH_BUSINESS_DESCRIPTION.includes(defaultBlockCategory)
        ? businessDescription
        : '');

    return {
      [fieldNames.BLOCK_CATEGORY]: defaultBlockCategory,
      [fieldNames.USER_INPUT]: defaultUserInput,
    };
  }, [
    supportedCategoriesForGeneration,
    initialBlockCategory,
    initialUserInput,
    businessDescription,
  ]);

  const { control, handleSubmit, watch, getValues, getFieldState, resetField } = useForm({
    defaultValues,
    mode: 'onTouched',
  });

  const selectedBlockCategory = watch(fieldNames.BLOCK_CATEGORY);

  const fields = useMemo(
    () => getFormFields({ control, supportedCategoriesForGeneration, selectedBlockCategory }),
    [control, supportedCategoriesForGeneration, selectedBlockCategory]
  );

  useEffect(() => {
    if (!isFirstRun.current) {
      isFirstRun.current = true;
      return;
    }

    if (selectedBlockCategory) {
      resetField(fieldNames.USER_INPUT, {
        defaultValue: CATEGORIES_WITH_BUSINESS_DESCRIPTION.includes(selectedBlockCategory)
          ? businessDescription
          : '',
      });
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [selectedBlockCategory]);

  useEffect(
    () => () => {
      const currentBlockCategory = getValues(fieldNames.BLOCK_CATEGORY);
      const currentUserInput = getValues(fieldNames.USER_INPUT);
      if (currentBlockCategory && currentUserInput && currentUserInput !== businessDescription) {
        setCachedUserInput(currentBlockCategory, currentUserInput);
      } else {
        clearCachedUserInput();
      }
    },
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    []
  );

  return {
    fields,
    handleSubmit,
    isSubmitDisabled: !selectedBlockCategory,
    getFieldState,
  };
};

export default useBlockGenerationForm;
